import {
    InvitationMethodField,
    invitationMethodFor,
} from 'src/components/AccountAccess/InvitationMethodField';
import {
    getOrganizationId,
    isOrganizationAdministrator,
} from 'src/domain/user';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import container from 'src/container';
import { useHistory } from 'react-router-dom';
import { useNotification } from 'src/components/Notification/useNotfication';
import {
    IsString,
    IsIn,
    IsEnum,
    IsBoolean,
    Length,
    IsNotEmpty,
    IsEmail,
    IsOptional,
    ValidateIf,
    IsArray,
    ArrayNotEmpty,
    ArrayUnique,
    IsUUID,
    Validate,
    ValidatorConstraint,
    ValidatorConstraintInterface,
    ValidationArguments,
    isPhoneNumber,
} from 'class-validator';
import Input from 'src/components/FormComponents/Input';
import Form from 'src/components/FormComponents/Form';
import { PeopleSVG } from 'src/assets/svg';
import { EnvelopeIcon } from 'src/components/Icons/icons';
import PhoneInput from 'src/components/FormComponents/PhoneInput';
import { CreatorRoleSelect } from 'src/components/FormComponents/CreatorRoleSelect';
import { useTranslation } from 'react-i18next';
import CheckBox from 'src/components/FormComponents/CheckBox';
import { EmployeeDepartmentSelect } from 'src/components/FormComponents/EmployeeDepartmentSelect';
import { EmployeeTypeSelect } from 'src/components/FormComponents/EmployeeTypeSelect';
import { EmployeeType } from 'src/domain/creator';

const {
    cradle: { munduClient },
} = container;

const hasContactValue = (value?: string): value is string =>
    typeof value === 'string' && value.trim().length > 0;

@ValidatorConstraint({ name: 'emailOrValidPhone', async: false })
class EmailOrValidPhoneConstraint implements ValidatorConstraintInterface {
    validate(phoneNumber: string | undefined, args: ValidationArguments) {
        const { email } = args.object as CreatorSchema;

        if (!hasContactValue(email) && !hasContactValue(phoneNumber)) {
            return false;
        }

        if (!hasContactValue(phoneNumber)) {
            return true;
        }

        return isPhoneNumber(phoneNumber, 'NO');
    }

    defaultMessage(args: ValidationArguments) {
        const { email } = args.object as CreatorSchema;
        const phoneNumber = args.value as string | undefined;

        if (!hasContactValue(email) && !hasContactValue(phoneNumber)) {
            return 'Epost eller telefonnummer må fylles ut.';
        }

        return 'Telefonnr er ikke gyldig';
    }
}

class CreatorSchema {
    @IsOptional()
    @IsIn(['email', 'phone'])
    invitationMethod?: 'email' | 'phone';
    @IsEnum(EmployeeType)
    employeeType: EmployeeType;

    @IsOptional()
    @IsString()
    departmentGroupId?: string;

    @IsString()
    @Length(2, 30, { message: 'Må være mellom 2 og 30 tegn' })
    @IsNotEmpty({ message: 'Må fylles ut.' })
    firstName: string;

    @IsString()
    @IsNotEmpty({ message: 'Må fylles ut.' })
    lastName: string;

    @IsString()
    @Validate(EmailOrValidPhoneConstraint)
    phoneNumber?: string;

    @IsOptional()
    @ValidateIf(o => o.email?.length !== 0)
    @IsEmail({}, { message: 'Epost er ikke gyldig' })
    email?: string;

    @IsArray()
    @ArrayNotEmpty({ message: 'Må fylles ut.' })
    @ArrayUnique()
    @IsUUID('all', { each: true })
    roleIds: string[];

    @IsOptional()
    @IsBoolean()
    organizationAdmin?: boolean;
}

export const NewCreator = () => {
    const history = useHistory();
    const user = useSignedInUser();
    const notify = useNotification();
    const { t } = useTranslation();
    if (!user) return null;
    const organizationId = getOrganizationId(user);
    const canManageAdmin = isOrganizationAdministrator(user, organizationId);

    const handleSubmit = async (data: CreatorSchema) => {
        try {
            const email = data.email?.trim() || undefined;
            const phoneNumber = data.phoneNumber || undefined;

            await munduClient.createCreator({
                email,
                phoneNumber,
                invitationMethod: invitationMethodFor(
                    email,
                    phoneNumber,
                    data.invitationMethod,
                ),
                profile: {
                    firstName: data.firstName,
                    lastName: data.lastName,
                },
                organizationId,
                departmentGroupId: data.departmentGroupId || undefined,
                employeeType: data.employeeType,
                followers: {
                    consumerIds: [],
                },
                roleIds: data.roleIds?.length ? data.roleIds : undefined,
                organizationAdmin:
                    canManageAdmin && data.organizationAdmin === true,
            });
            notify(t('creators.notifications.created'));
            history.push('/app/creators');
        } catch (e) {
            if (e instanceof Error) notify(e.message, true);
        }
    };

    return (
        <div className="flex flex-col max-w-3xl mx-auto">
            <Form
                className="bg-lightPeach text-woodyBrown"
                schema={CreatorSchema}
                onSubmit={handleSubmit}
                mode="onChange"
                defaultValues={{
                    organizationAdmin: false,
                    departmentGroupId: '',
                    employeeType: EmployeeType.fullTime,
                }}
            >
                {({
                    register,
                    control,
                    clearErrors,
                    formState: { isValid, errors, isSubmitting },
                }) => (
                    <div className="grid grid-cols-2 gap-4 h-full">
                        <div className="col-span-full flex justify-center">
                            {/* TODO: Replace with Role icon when ready */}
                            <div className="rounded-full w-36 h-36 bg-starkWhite flex justify-center items-center mb-4">
                                <PeopleSVG className="w-[18px] h-[18px]" />
                            </div>
                        </div>

                        <Input
                            className="text-input"
                            type="text"
                            label={t('creators.form.firstName')}
                            error={errors.firstName}
                            {...register('firstName')}
                        />
                        <Input
                            className="text-input"
                            type="text"
                            label={t('creators.form.lastName')}
                            error={errors.lastName}
                            {...register('lastName')}
                        />
                        <PhoneInput
                            className="text-input"
                            type="tel"
                            error={errors.phoneNumber}
                            label={t('creators.form.phone')}
                            {...register('phoneNumber')}
                        />
                        <Input
                            className="text-input"
                            type="email"
                            label={t('creators.form.email')}
                            error={errors.email}
                            leadingIcon={EnvelopeIcon}
                            {...register('email')}
                        />
                        <InvitationMethodField control={control} />
                        <EmployeeDepartmentSelect
                            control={control}
                            name="departmentGroupId"
                            organizationId={organizationId}
                            disabled={isSubmitting || Boolean(user.supportMode)}
                        />
                        <EmployeeTypeSelect
                            control={control}
                            name="employeeType"
                            disabled={isSubmitting || Boolean(user.supportMode)}
                        />
                        <CreatorRoleSelect
                            control={control}
                            name={'roleIds'}
                            organizationId={organizationId}
                            error={errors.roleIds}
                            clearErrors={clearErrors}
                        />
                        {canManageAdmin && (
                            <CheckBox
                                className="col-span-full"
                                label={t('roles.systemAdmin')}
                                helperText={t('creators.form.adminDescription')}
                                {...register('organizationAdmin')}
                            />
                        )}

                        <div className="col-span-full">
                            <Input
                                value={t('creators.form.submit')}
                                className="primary-btn-small"
                                disabled={isSubmitting}
                                type="submit"
                            />
                        </div>
                    </div>
                )}
            </Form>
        </div>
    );
};
